import { useLayoutEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';

import { useStores } from '../../stores/useStores';
import { subscribeAppViewportScroll } from '../../utils/appScroll';
import { checkAppearanceLimitLeft } from '../../utils/liveBannerAppearanceTracker';

import { LiveBannerDisplay } from './LiveBannerDisplay';
import { clampLiveBannerHeightPx, getLiveBannerTopInset } from './liveBannerTopInset';

/** Set on `document.documentElement` when the live banner is visible; `0px` when hidden. */
export const LIVE_BANNER_HEIGHT_CSS_VAR = '--live-banner-height';

function setLiveBannerHeightCssVar(px: number) {
  document.documentElement.style.setProperty(LIVE_BANNER_HEIGHT_CSS_VAR, `${px}px`);
}

function syncLiveBannerHeightFromElement(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const maxHeight = Math.max(0, Math.round(rect.height));
  const inset = getLiveBannerTopInset(rect);

  setLiveBannerHeightCssVar(clampLiveBannerHeightPx(inset, maxHeight));
}

const LiveBanner = observer(() => {
  const { uiStore, userStore } = useStores();
  const { bannerMessage, showCloseButton, showLogo, bannerKey, appearanceLimit } = uiStore.liveBannerData;
  const userId = userStore.profile?.firebaseUid;

  const containerRef = useRef<HTMLDivElement>(null);
  const [appearanceAllowed, setAppearanceAllowed] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!bannerMessage) {
      setAppearanceAllowed(false);
      return;
    }
    if (appearanceLimit > 0 && bannerKey) {
      const isAppearanceLimit = checkAppearanceLimitLeft(userId, bannerKey, appearanceLimit);
      setAppearanceAllowed(isAppearanceLimit);
    } else {
      setAppearanceAllowed(true);
    }
  }, [bannerMessage, appearanceLimit, bannerKey, userId]);

  const showBanner = bannerMessage != null && bannerMessage !== '' && appearanceAllowed;

  useLayoutEffect(() => {
    if (!showBanner) {
      setLiveBannerHeightCssVar(0);
      return;
    }

    const el = containerRef.current;
    if (!el) {
      return;
    }

    let rafId = 0;

    const sync = () => {
      syncLiveBannerHeightFromElement(el);
    };

    const scheduleSync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sync);
    };

    sync();

    const ro = new ResizeObserver(scheduleSync);
    ro.observe(el);

    const unsubscribeScroll = subscribeAppViewportScroll(scheduleSync);
    window.addEventListener('resize', scheduleSync, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      unsubscribeScroll();
      window.removeEventListener('resize', scheduleSync);
      setLiveBannerHeightCssVar(0);
    };
  }, [showBanner, showLogo, showCloseButton, bannerMessage]);

  if (!showBanner) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <LiveBannerDisplay
        bannerMessage={bannerMessage}
        showLogo={showLogo}
        onCloseClick={showCloseButton ? () => uiStore.resetLiveBannerData() : undefined}
      />
    </div>
  );
});

export default LiveBanner;
